import { useCallback, useRef } from 'react';

import { useRouter } from 'next/router';
import QueryString from 'qs';

import usePageLeave, { PageLeaveType } from 'src/hooks/usePageLeave';
import useSelectedTab from 'src/hooks/useSelectedTab';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';

/**
 * 브라우저 이탈 시 세션 종료 이벤트를 발행하는 훅.
 *
 * screen_opened 이벤트와 동일한 파라미터 구조를 사용하며,
 * 추가로 leave_type(감지 방법)과 duration(체류 시간, 초)을 포함한다.
 *
 * duration은 라우트 변경 또는 탭 복귀 시점부터 이탈까지의 실제 체류 시간을 측정한다.
 */
export default function useSessionEnd() {
  const router = useRouter();
  const { selectedTab } = useSelectedTab();
  const mountTimeRef = useRef(Date.now());

  const handlePageLeave = useCallback(
    (leaveType: PageLeaveType) => {
      const durationMs = Date.now() - mountTimeRef.current;
      const duration = Math.round(durationMs / 100) / 10;

      pushEvent({
        eventType: EVENT_TYPE.DEBUG,
        eventName: EVENT_NAME.DEBUG__SESSION_END,
        eventParams: {
          tab: selectedTab.name,
          path: router.pathname,
          queryParams: QueryString.stringify(router.query),
          leave_type: leaveType,
          duration,
        },
      });
    },
    [selectedTab.name, router.pathname, router.query]
  );

  usePageLeave(handlePageLeave);
}
